'use client'

import PopupArrow from '../Popup/PopupArrow'
import TextInputWrapper from '../TextInputWrapper'
import classNames from 'classnames'
import styles from './ColorInput.module.css'
import { ColorInputProps, PopoverPickerProps, type SwatchProps } from './types'
import { Dropdown } from '../Dropdown'
import { HexColorInput } from 'react-colorful'
import {
  type JSX,
  type KeyboardEventHandler,
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useId,
  useRef,
} from 'react'
import { TRANSPARENT_COLOR_CODE } from '@betterplace/utils'
import { buildBackgroundStyles, getTokenColorHex } from './helpers'
import { useDropdownContext } from '../Dropdown/Dropdown'

/**
 * TODO DEV-26305:
 * - Add proper labels (international)
 */
const Swatch = forwardRef(function Swatch(
  {
    onClick,
    color,
    triggerSwatch = false,
    'className': externalClassName,
    'aria-describedby': ariaDescribedBy,
    ...props
  }: SwatchProps,
  ref: React.Ref<HTMLButtonElement>
) {
  return (
    <button
      {...props}
      className={classNames(styles.swatch, externalClassName, {
        [styles.activeSwatch as string]: triggerSwatch,
      })}
      onClick={onClick}
      ref={ref}
      type="button"
      // This is the role of <input type='color' /> and we are trying to mimic some of its functionality
      aria-roledescription={triggerSwatch ? 'colour well' : 'button'}
      aria-describedby={ariaDescribedBy}
    >
      <div
        style={{
          background: buildBackgroundStyles(color),
        }}
        className={styles.swatchInner}
      ></div>

      {/* DEV-26305 This is not really accessible, we should think about how to approach this */}
      <span className="sr-only">{color}</span>
    </button>
  )
})

function PopoverPicker({
  color,
  presetColors,
  onChange,
  hexInputLabel,
  label,
  id: id_,
  classNames: externalStyles,
  error,
  ...props
}: PopoverPickerProps) {
  const generatedId = useId()
  const id = id_ || props.name || generatedId
  const modalContent = useRef(null)
  const popoverTriggerRef = useRef<HTMLButtonElement>(null)

  return (
    <TextInputWrapper
      name={props.name}
      id={id}
      label={label}
      error={error}
      className={classNames(styles.picker, externalStyles?.label)}
      errorDisplay="border"
    >
      <Dropdown
        className={styles.dropdown}
        trigger={
          <Swatch
            {...props}
            aria-describedby={`label-${id}`}
            id={id}
            color={color}
            ref={popoverTriggerRef}
            triggerSwatch
            className={classNames(externalStyles?.trigger, { [styles.errorSwatch!]: error })}
          />
        }
        Anchor={PopupArrow}
      >
        <div className={styles.popover} ref={modalContent}>
          <div className={styles.pickerSwatches}>
            {presetColors.map((presetColor) => {
              return (
                <SwatchItem
                  {...props}
                  key={presetColor}
                  color={presetColor}
                  onClick={() => {
                    onChange(presetColor)
                  }}
                  className={classNames({
                    [styles.activeSwatch!]: presetColor === color,
                    [styles.inactiveSwatch as string]: presetColor !== color,
                  })}
                />
              )
            })}

            <DropdownHexColorInput id={id} color={color} onChange={onChange} alpha prefixed className={styles.input} />
          </div>
        </div>
      </Dropdown>
    </TextInputWrapper>
  )
}

function SwatchItem({ onClick: onClick_, ...props }: SwatchProps) {
  const { hidePopup } = useDropdownContext()
  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (ev) => {
      hidePopup()
      onClick_?.(ev)
    },
    [onClick_, hidePopup]
  )
  return <Swatch {...props} onClick={onClick} />
}

type HexColorInputProps = Parameters<typeof HexColorInput>[0]
function DropdownHexColorInput({ onKeyDown: onKeyDown_, ...props }: HexColorInputProps) {
  const { hidePopup } = useDropdownContext()
  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (ev) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      onKeyDown_?.(ev)
      if (ev.key !== 'Enter') return
      hidePopup()
    },
    [onKeyDown_, hidePopup]
  )
  return <HexColorInput {...props} onKeyDown={onKeyDown} />
}

function ColorInput({
  onColorChange,
  transparentOption = false,
  value,
  hexInputLabel,
  label,
  ...props
}: ColorInputProps): JSX.Element {
  const presetColors = [
    getTokenColorHex('betterplace-color-light'),
    getTokenColorHex('betterplace-color-green-500'),
    getTokenColorHex('betterplace-color-green-700'),
    getTokenColorHex('betterplace-color-green-900'),
    getTokenColorHex('betterplace-color-yellow-400'),
    getTokenColorHex('betterplace-me-color-orange-500'),
    getTokenColorHex('betterplace-color-red-700'),
    getTokenColorHex('betterplace-color-red-900'),
    getTokenColorHex('betterplace-color-purple-400'),
    getTokenColorHex('betterplace-color-purple-700'),
    getTokenColorHex('betterplace-color-gray-500'),
    getTokenColorHex('betterplace-color-gray-700'),
    getTokenColorHex('betterplace-color-gray-900'),
  ].filter(Boolean)

  if (transparentOption) {
    presetColors.push(`#${TRANSPARENT_COLOR_CODE}`)
  }

  return (
    <PopoverPicker
      {...props}
      color={`#${value === 'transparent' ? TRANSPARENT_COLOR_CODE : value}`}
      // TODO: this is probably because we have no types in design tokens atm
      presetColors={presetColors as unknown as string[]}
      onChange={onColorChange}
      hexInputLabel={hexInputLabel}
      label={label}
    />
  )
}

export default ColorInput
